import React, { lazy } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { List, Map } from 'immutable'
import _get from 'lodash/get'
import _omit from 'lodash/omitBy'
import _includes from 'lodash/includes'
import _isUndefined from 'lodash/isUndefined'
import Button, { TYPES } from '../../Button/index'
import { H1 } from '../../Heading/index'
import { getBoundActions } from '../../../actions/index'
import {
  LAUNCH_PAGE,
  TOPICS_PAGE,
  PRACTICE_PAGE,
  DURATION_PAGE,
  FINAL_PAGE,
  AI_PAGE,
  ADD_TO_PLAYLIST_PAGE,
  SCREEN_MAP,
  PRACTICES_ARRAY,
  formatPost,
} from '../../../services/onboarding/index'
import { URL_HOME } from '../../../services/url/constants'
import { get as getConfig } from '../../../config/index'
import { historyRedirect } from '../../../services/navigation/index'
import {
  ONBOARDING_STARTED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import { checkUserHasContentImpression } from '../../../services/inbound-tracking/index'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/GaiaLogo'
import { ERROR_TYPE_500 } from '../../ErrorPage/types'
import Sherpa, { TYPE_LARGE } from '../../Sherpa/index'
import OptimizelyFeature from '../../OptimizelyFeature/index'
import { deactivateSubject } from '../../../services/testarossa/index'
import AppStoreButtons, { MODES } from '../../AppStoreButtons/AppStoreButtons'
import OnboardingTopics from '../OnboardingTopics'
import OnboardingPractice from '../OnboardingPractice'
import OnboardingDuration from '../OnboardingDuration'
import OnboardingFinal from '../OnboardingFinal'
import OnboardingAI from '../OnboardingAI'
import OnboardingAddToPlaylist from '../OnboardingAddToPlaylist'

// for progress bar
const SCREENS_FITNESS_MEDITATION = 3
const SCREENS_OTHER = 2
const SCREENS_YOGA = 4

function getPage ({ screenNumber, topTopic, page }) {
  if (topTopic && page !== ADD_TO_PLAYLIST_PAGE) {
    const type = topTopic.get('type')
    return SCREEN_MAP[type][screenNumber]
  }
  if (screenNumber === 0) {
    return LAUNCH_PAGE
  }
  return page
}

const LoadableErrorPage = lazy(
  () => import('../../ErrorPage/index'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

class Onboarding extends React.Component {
  constructor () {
    super()
    this.state = {
      topTopic: null,
      selectedTopics: List(),
      page: LAUNCH_PAGE,
      options: List(),
      screenNumber: 0,
      progress: 0.25,
      duration: 0,
      posted: false,
    }
  }

  componentDidMount () {
    const {
      getV5OnboardingData,
      language,
      auth,
      history,
      setFeatureTrackingDataPersistent,
      onboarding,
      location,
    } = this.props

    if (
      !auth.get('jwt')
      || (onboarding.get('completed') === true
      && onboarding.get('processing') !== true
      && location.query.obTest !== 'true')
    ) {
      historyRedirect({
        url: URL_HOME,
        language,
        history,
        auth,
      })
    }

    getV5OnboardingData(language)
    deactivateSubject({ campaign: 'WA-10' })

    /* Remove this statement and all deps, props when tour component is removed or disabled */
    if (this.props.tourStatusCode !== 2) {
      setFeatureTrackingDataPersistent({
        data: Map({
          navTourTooltips: 2,
        }),
        auth,
      })
    }
    /** Remove this statement and all deps,
    * props when announcement modal component is removed or disabled
    * */
    if (!!getConfig().features.announcementModal.enabled && !this.props.announcementModal) {
      setFeatureTrackingDataPersistent({
        data: Map({
          announcementModal: true,
        }),
        auth,
      })
    }
  }

  componentWillUnmount () {
    clearInterval(this.interval)
  }

  setTopics = (topic) => {
    const { selectedTopics } = this.state
    let newTopics

    if (selectedTopics.includes(topic)) {
      newTopics = selectedTopics.filter((item) => item !== topic)
    } else if (selectedTopics.size === 3) {
      return null
    } else {
      newTopics = selectedTopics.push(topic)
    }

    const topicType = newTopics.getIn([0, 'type'])
    let totalScreens = SCREENS_OTHER

    if (topicType === 'yoga') totalScreens = SCREENS_YOGA
    if (topicType === 'fitness' || topicType === 'meditation') totalScreens = SCREENS_FITNESS_MEDITATION

    return this.setState({
      selectedTopics: newTopics,
      topTopic: newTopics.get(0),
      totalScreens,
    })
  }

  setPage = (forward = true, data = {}) => {
    this.setState((state) => {
      const { screenNumber, totalScreens } = state
      const nextState = {
        selectedTopics: _get(data, 'selectedTopics'),
        progress: _get(state, 'progress'),
        duration: _get(data, 'duration'),
        options: _get(data, 'options'),
        screenNumber: screenNumber + 1,
        posted: false,
      }
      if (forward !== true) {
        nextState.screenNumber = screenNumber - 1
      }
      if (totalScreens) {
        nextState.progress = nextState.screenNumber / totalScreens
      }
      return _omit(nextState, _isUndefined)
    }, this.postData)

    window.scroll(0, 0)
  }

  setOptions = (option) => {
    const { options, screenNumber } = this.state

    // filter out duplicates from same screen
    const filterdOptions = options.filter((item) => item.get('screenNumber') !== screenNumber)
    const newOption = option.set('screenNumber', screenNumber)
    const newOptions = filterdOptions.push(newOption)

    // this.setState({ options: newOptions })
    this.setPage(true, {
      options: newOptions,
    })
  }

  setDuration = (duration) => {
    this.setState({ duration })
  }

  /**
   * Continue from AI page to Final with genericTerm. Runs postData.
   */
  continueToFinalWithGenericTerm = (genericTerm) => {
    const term = (genericTerm && Map.isMap(genericTerm)) ? genericTerm : Map({ type: 'other' })
    const topicType = term.get('type')
    let totalScreens = SCREENS_OTHER
    let finalScreenNumber = 2
    if (topicType === 'yoga') {
      totalScreens = SCREENS_YOGA
      finalScreenNumber = 4
    } else if (topicType === 'fitness' || topicType === 'meditation') {
      totalScreens = SCREENS_FITNESS_MEDITATION
      finalScreenNumber = 3
    }
    this.setState({
      selectedTopics: List([term]),
      topTopic: term,
      totalScreens,
      screenNumber: finalScreenNumber,
      progress: finalScreenNumber / totalScreens,
      posted: false,
    }, this.postData)
    window.scroll(0, 0)
  }

  postData = () => {
    const { state, props } = this
    const nextPage = getPage(state)

    if (nextPage === FINAL_PAGE || nextPage === ADD_TO_PLAYLIST_PAGE) {
      const { selectedTopics, options, duration } = state
      const { postV5OnboardingData, showProfilePrompt, auth } = props
      const terms = formatPost(selectedTopics, options, duration)
      postV5OnboardingData({ terms, auth })
      showProfilePrompt(false)
      this.setState({ posted: true })
    }
  }

  /**
   * Continue to playlist page from topics or practice page.
   * @param {Map} option - The option to add to the playlist.
   */
  handleContinueToPlaylist = (option, force = false) => {
    if (force) {
      this.setState({ page: ADD_TO_PLAYLIST_PAGE })
      window.scroll(0, 0)
      return
    }

    const { options, screenNumber, topTopic } = this.state
    const topic = Map.isMap(topTopic) ? topTopic.get('type') : null

    let newOptions = options

    if (option) {
      const filterdOptions = options.filter((item) => item.get('screenNumber') !== screenNumber)
      const newOption = option.set('screenNumber', screenNumber)
      newOptions = filterdOptions.push(newOption)
    }

    if ((topic === 'yoga' && screenNumber === 3)
      || (topic === 'fitness' && screenNumber === 2)
      || (topic === 'meditation' && screenNumber === 2)
      || topic === 'other'
    ) {
      this.setState({ page: ADD_TO_PLAYLIST_PAGE, options: newOptions }, this.postData)
      window.scroll(0, 0)
      return
    }

    this.setPage(true, { options: newOptions })
  }

  renderLaunch = () => {
    const { copy, setEventDefault, staticText } = this.props

    if (copy === undefined) {
      return null
    }

    return (
      <React.Fragment>
        <div className={`onboarding__launch ${this.props.language}`}>
          <div className="onboarding__launch__container">
            <div className="onboarding__gradient-bar" />
            <div className="onboarding__launch-gradient">
              <div className="onboarding__launch-content">
                <div className="onboarding__logo-container">
                  <GaiaLogo
                    type={TYPE_TEAL}
                    className={['onboarding__logo']}
                  />
                </div>
                <div className="onboarding__intro-text-wrapper">
                  <H1 className="onboarding__intro-title">
                    { copy.get('introTitle') }
                    <br />{copy.get('introSubtitle')}</H1>
                  <p className="onboarding__intro-text">{copy.get('introParagraph1')}</p>
                </div>
                <>
                  <OptimizelyFeature
                    flag={'onboarding_ai_v2'}
                    variation="control"
                    isDefault={true}
                    anon={false}
                    noWrapper
                  >
                    <Button
                      type={TYPES.PRIMARY}
                      onClick={() => {
                        this.setPage(true)
                        this.setState({ page: TOPICS_PAGE })
                        setEventDefault(ONBOARDING_STARTED_GA4_EVENT.mergeDeep({
                          ui_location: 'onboarding',
                        }))
                      }}
                      className="onboarding__button"
                    >
                      {copy.get('introLetsGo')}
                    </Button>
                  </OptimizelyFeature>
                  <OptimizelyFeature
                    flag={'onboarding_ai_v2'}
                    variation={'ai_variation'}
                    noWrapper
                  >
                    <Button
                      type={TYPES.PRIMARY}
                      onClick={() => {
                        this.setPage(true)
                        this.setState({ page: AI_PAGE })
                        setEventDefault(ONBOARDING_STARTED_GA4_EVENT.mergeDeep({
                          ui_location: 'AI onboarding',
                        }))
                      }}
                      className="onboarding__button"
                    >
                      {copy.get('introLetsGo')}
                    </Button>
                  </OptimizelyFeature>
                </>
              </div>
              <AppStoreButtons
                mode={MODES.APP_STORE_BUTTONS_LIGHT}
                text={staticText.get('appStoreButtonsText')}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  renderTopics = ({ hasContentImpression }) => {
    const {
      props,
      state,
      setTopics,
      setPage,
      handleContinueToPlaylist,
    } = this
    const {
      selectedTopics,
      progress,
      screenNumber,
    } = state
    const handleBackFromTopics = () => this.setPage(false)

    return (
      <OnboardingTopics
        setTopics={setTopics}
        setPage={setPage}
        selectedTopics={selectedTopics}
        progress={progress}
        screenNumber={screenNumber}
        copy={props.copy}
        hasContentImpression={hasContentImpression}
        handleBackClick={handleBackFromTopics}
        onContinueToPlaylist={handleContinueToPlaylist}
      />
    )
  }

  renderPractice = ({ hasContentImpression }) => {
    const {
      props,
      state,
      setPage,
      setOptions,
      handleContinueToPlaylist,
    } = this
    const { topTopic, screenNumber, progress } = state

    return (
      <OnboardingPractice
        setPage={setPage}
        topic={topTopic.get('type')}
        setOptions={setOptions}
        screenNumber={screenNumber}
        progress={progress}
        copy={props.copy}
        hasContentImpression={hasContentImpression}
        onContinueToPlaylist={handleContinueToPlaylist}
      />
    )
  }

  renderDuration = () => {
    return (
      <OnboardingDuration
        setPage={this.setPage}
        progress={this.state.progress}
        screenNumber={this.state.screenNumber}
        setDuration={this.setDuration}
        copy={this.props.copy}
      />
    )
  }

  renderFinal = () => {
    const { props, state } = this
    const { topTopic } = state
    const { history } = props

    const type = Map.isMap(topTopic) ? topTopic.get('type') : 'other'
    const isPractice = _includes(PRACTICES_ARRAY, type)

    return (
      <OnboardingFinal
        history={history}
        copy={props.copy}
        isPractice={isPractice}
      />
    )
  }

  renderAddToPlaylist = () => {
    const { props, state } = this
    return (
      <OnboardingAddToPlaylist
        screenNumber={state.screenNumber}
        copy={props.copy}
        staticText={props.staticText}
        onContinueToFinal={() => {
          this.setPage()
          this.setState({ page: FINAL_PAGE })
        }}
      />
    )
  }

  renderPage () {
    const { state, props } = this
    const page = getPage(state)
    const hasContentImpression = checkUserHasContentImpression(props.inboundTracking)

    switch (page) {
      case DURATION_PAGE:
        return this.renderDuration()
      case PRACTICE_PAGE:
        return this.renderPractice({ hasContentImpression })
      case TOPICS_PAGE:
        return this.renderTopics({ hasContentImpression })
      case LAUNCH_PAGE:
        return this.renderLaunch()
      case FINAL_PAGE:
        return this.renderFinal()
      case ADD_TO_PLAYLIST_PAGE:
        return this.renderAddToPlaylist()
      case AI_PAGE:
        return (
          <OnboardingAI
            setPage={this.setPage}
            setTopics={this.setTopics}
            onSkipToTopics={() => this.setState({ page: TOPICS_PAGE })}
            onGoHome={async () => {
              historyRedirect({
                history: this.props.history,
                url: URL_HOME,
                auth: this.props.auth,
                language: this.props.language,
              })
            }}
            onContinueToFinal={this.continueToFinalWithGenericTerm}
            language={this.props.language}
            staticText={this.props.staticText}
            hasContentImpression={hasContentImpression}
            onContinueToPlaylist={this.handleContinueToPlaylist}
          />
        )
      default:
        return null
    }
  }

  render () {
    const { props } = this
    const { location } = props

    if (props.error) {
      return (
        <LoadableErrorPage
          code={ERROR_TYPE_500}
          location={location}
        />
      )
    }
    return (
      <div className="onboarding">
        {this.renderPage()}
      </div>
    )
  }
}

export default compose(
  connectRedux(
    (state) => {
      return {
        copy: state.onboarding.getIn(['v5Data', 'screenCopy', 0]),
        language: state.user.getIn(['data', 'language', 0]),
        error: state.onboarding.get('error'),
        auth: state.auth,
        tourStatusCode: state.featureTracking.getIn(['data', 'navTourTooltips']),
        announcementModal: state.featureTracking.getIn(['data', 'announcementModal']),
        staticText: state.staticText.getIn(['data', 'onboarding', 'data']),
        inboundTracking: state.inboundTracking,
        onboarding: state.onboarding,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        postV5OnboardingData: actions.onboarding.postV5OnboardingData,
        getV5OnboardingData: actions.onboarding.getV5OnboardingData,
        showProfilePrompt: actions.userProfiles.showPrompt,
        setFeatureTrackingDataPersistent: actions.featureTracking.setFeatureTrackingDataPersistent,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(Onboarding)
